import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, Filter, X, Heart, MapPin, Check } from "lucide-react";
import axios from "axios";
import { logger } from "@/utils/logger";

const API = process.env.REACT_APP_BACKEND_URL;

const SPECIES_TABS = [
  { value: "", label: "All", icon: "🐾" },
  { value: "canine", label: "Dogs", icon: "🐕" },
  { value: "feline", label: "Cats", icon: "🐱" },
  { value: "equine", label: "Horses", icon: "🐴" },
  { value: "exotics", label: "Exotics", icon: "🦜" },
  { value: "others", label: "Farm Animals", icon: "🐖" },
];

const Adoptions = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  
  const activeSpecies = searchParams.get("species") || "";
  const [filters, setFilters] = useState({
    breed: searchParams.get("breed") || "",
    state: searchParams.get("state") || "",
    max_fee: searchParams.get("max_fee") || "",
    good_with_kids: searchParams.get("good_with_kids") === "true",
    good_with_dogs: searchParams.get("good_with_dogs") === "true",
    good_with_cats: searchParams.get("good_with_cats") === "true",
  });

  useEffect(() => {
    fetchAdoptions();
  }, [searchParams]);

  const fetchAdoptions = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (activeSpecies) params.append("species", activeSpecies);
      if (filters.breed) params.append("breed", filters.breed);
      if (filters.state) params.append("state", filters.state);
      if (filters.max_fee) params.append("max_fee", filters.max_fee);
      if (filters.good_with_kids) params.append("good_with_kids", "true");
      if (filters.good_with_dogs) params.append("good_with_dogs", "true");
      if (filters.good_with_cats) params.append("good_with_cats", "true");

      const res = await axios.get(`${API}/api/browse/adoptions?${params.toString()}`);
      setListings(res.data.listings || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      logger.error("Failed to fetch adoptions:", err);
      setListings([]);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (species) => {
    const newParams = new URLSearchParams(searchParams);
    if (species) {
      newParams.set("species", species);
    } else {
      newParams.delete("species");
    }
    setSearchParams(newParams);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    const newParams = new URLSearchParams();
    if (activeSpecies) newParams.set("species", activeSpecies);
    if (filters.breed) newParams.set("breed", filters.breed);
    if (filters.state) newParams.set("state", filters.state);
    if (filters.max_fee) newParams.set("max_fee", filters.max_fee);
    if (filters.good_with_kids) newParams.set("good_with_kids", "true");
    if (filters.good_with_dogs) newParams.set("good_with_dogs", "true");
    if (filters.good_with_cats) newParams.set("good_with_cats", "true");
    setSearchParams(newParams);
    setShowFilters(false);
  };

  const clearFilters = () => {
    setFilters({ breed: "", state: "", max_fee: "", good_with_kids: false, good_with_dogs: false, good_with_cats: false });
    const newParams = new URLSearchParams();
    if (activeSpecies) newParams.set("species", activeSpecies);
    setSearchParams(newParams);
    setShowFilters(false);
  };

  return (
    <div className="pt-24 pb-16 min-h-screen bg-[#050505]">
      {/* Hero Section */}
      <section className="px-6 md:px-12 mb-12">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <Heart className="w-8 h-8 text-[#F472B6]" />
            <p className="text-xs tracking-[0.3em] uppercase text-[#F472B6]">Find Your Forever Friend</p>
          </div>
          <h1 className="text-4xl md:text-5xl text-[#EDEDED] mb-4">
            Animals for Adoption
          </h1>
          <p className="text-lg text-[#A1A1AA] max-w-2xl">
            Give a loving home to animals in need. Browse pets available for rehoming from verified owners and breeders.
          </p>
        </div>
      </section>

      {/* Species Tabs */}
      <section className="px-6 md:px-12 mb-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap gap-2 p-1 bg-[#18181B] rounded-xl w-fit">
            {SPECIES_TABS.map((tab) => (
              <button
                key={tab.value}
                onClick={() => handleTabChange(tab.value)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                  activeSpecies === tab.value
                    ? "bg-[#F472B6] text-black"
                    : "text-[#A1A1AA] hover:text-[#EDEDED] hover:bg-[#27272A]"
                }`}
                data-testid={`tab-${tab.value || 'all'}`}
              >
                <span>{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Filters Bar */}
      <section className="px-6 md:px-12 mb-8">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <p className="text-[#71717A] text-sm">
            {loading ? "Loading..." : `${total} ${total === 1 ? "animal" : "animals"} available`}
          </p>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-[#3F3F46] text-[#A1A1AA] hover:border-[#F472B6] hover:text-[#EDEDED] transition-colors"
          >
            <Filter className="w-4 h-4" />
            Filters
          </button>
        </div>
      </section>

      {/* Filter Panel */}
      {showFilters && (
        <section className="px-6 md:px-12 mb-8">
          <div className="max-w-7xl mx-auto">
            <div className="glass-card p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg text-[#EDEDED]">Filter Results</h3>
                <button onClick={() => setShowFilters(false)} className="text-[#71717A] hover:text-[#EDEDED]">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div>
                  <label className="block text-sm text-[#A1A1AA] mb-2">Breed</label>
                  <input
                    type="text"
                    value={filters.breed}
                    onChange={(e) => handleFilterChange("breed", e.target.value)}
                    placeholder="e.g., Labrador"
                    className="input-minimal"
                  />
                </div>
                <div>
                  <label className="block text-sm text-[#A1A1AA] mb-2">State</label>
                  <input
                    type="text"
                    value={filters.state}
                    onChange={(e) => handleFilterChange("state", e.target.value)}
                    placeholder="e.g., TX"
                    className="input-minimal"
                  />
                </div>
                <div>
                  <label className="block text-sm text-[#A1A1AA] mb-2">Max Rehoming Fee</label>
                  <input
                    type="number"
                    value={filters.max_fee}
                    onChange={(e) => handleFilterChange("max_fee", e.target.value)}
                    placeholder="$500"
                    className="input-minimal"
                  />
                </div>
              </div>
              
              {/* Compatibility Filters */}
              <div className="mb-6">
                <label className="block text-sm text-[#A1A1AA] mb-3">Compatibility</label>
                <div className="flex flex-wrap gap-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={filters.good_with_kids}
                      onChange={(e) => handleFilterChange("good_with_kids", e.target.checked)}
                      className="sr-only"
                    />
                    <span className={`px-3 py-1.5 rounded-lg border transition-colors ${
                      filters.good_with_kids 
                        ? "border-[#F472B6] bg-[#F472B6]/10 text-[#F472B6]" 
                        : "border-[#3F3F46] text-[#A1A1AA]"
                    }`}>
                      {filters.good_with_kids && <Check className="w-4 h-4 inline mr-1" />}
                      Good with Kids
                    </span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={filters.good_with_dogs}
                      onChange={(e) => handleFilterChange("good_with_dogs", e.target.checked)}
                      className="sr-only"
                    />
                    <span className={`px-3 py-1.5 rounded-lg border transition-colors ${
                      filters.good_with_dogs 
                        ? "border-[#F472B6] bg-[#F472B6]/10 text-[#F472B6]" 
                        : "border-[#3F3F46] text-[#A1A1AA]"
                    }`}>
                      {filters.good_with_dogs && <Check className="w-4 h-4 inline mr-1" />}
                      Good with Dogs
                    </span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={filters.good_with_cats}
                      onChange={(e) => handleFilterChange("good_with_cats", e.target.checked)}
                      className="sr-only"
                    />
                    <span className={`px-3 py-1.5 rounded-lg border transition-colors ${
                      filters.good_with_cats 
                        ? "border-[#F472B6] bg-[#F472B6]/10 text-[#F472B6]" 
                        : "border-[#3F3F46] text-[#A1A1AA]"
                    }`}>
                      {filters.good_with_cats && <Check className="w-4 h-4 inline mr-1" />}
                      Good with Cats
                    </span>
                  </label>
                </div>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={applyFilters}
                  className="px-6 py-2 bg-[#F472B6] text-black rounded-lg hover:bg-[#F9A8D4] transition-colors"
                >
                  Apply Filters
                </button>
                <button
                  onClick={clearFilters}
                  className="px-6 py-2 border border-[#3F3F46] text-[#A1A1AA] rounded-lg hover:border-[#71717A] transition-colors"
                >
                  Clear All
                </button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Listings Grid */}
      <section className="px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="w-8 h-8 text-[#F472B6] animate-spin" />
            </div>
          ) : listings.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {listings.map((listing) => (
                <AdoptionCard key={listing.listing_id} listing={listing} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#18181B] flex items-center justify-center">
                <Heart className="w-10 h-10 text-[#3F3F46]" />
              </div>
              <h3 className="text-xl text-[#EDEDED] mb-2">No Animals Available</h3>
              <p className="text-[#71717A] mb-8 max-w-md mx-auto">
                There are currently no animals available for adoption. Check back soon or browse other listings.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/browse/canine"
                  className="px-6 py-3 bg-[#F472B6] text-black rounded-lg hover:bg-[#F9A8D4] transition-colors"
                >
                  Browse All Pets
                </Link>
                <Link
                  to="/"
                  className="px-6 py-3 border border-[#3F3F46] text-[#EDEDED] rounded-lg hover:border-[#F472B6] transition-colors"
                >
                  Back to Home
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

const AdoptionCard = ({ listing }) => {
  const [imgError, setImgError] = useState(false);
  const fallbackImage = "https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=400&h=300&fit=crop";
  const imgSrc = imgError ? fallbackImage : (listing.photos?.[0] || fallbackImage);

  return (
    <Link
      to={`/listing/${listing.listing_id}`}
      className="group glass-card overflow-hidden hover:border-[#F472B6]/30 transition-all duration-300 block"
      data-testid={`adoption-${listing.listing_id}`}
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-[#18181B]">
        <img
          src={imgSrc}
          alt={listing.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
          onError={() => setImgError(true)}
        />
        <div className="absolute top-2 left-2 flex flex-col gap-1">
          <span className="px-2 py-1 bg-[#F472B6] text-black text-xs font-medium rounded-full flex items-center gap-1">
            <Heart className="w-3 h-3" /> Adoption
          </span>
          {listing.is_shelter && (
            <span className="px-2 py-1 bg-[#8B5CF6] text-white text-xs font-medium rounded-full">
              Shelter
            </span>
          )}
        </div>
        {listing.good_with_kids && (
          <div className="absolute top-2 right-2">
            <span className="px-2 py-1 bg-[#22C55E] text-black text-xs font-medium rounded-full">
              Kid-Friendly
            </span>
          </div>
        )}
      </div>
      <div className="p-4">
        <h4 className="text-lg text-[#EDEDED] mb-1 truncate">
          {listing.animal_name || listing.title}
        </h4>
        <p className="text-[#F472B6] font-medium mb-2">
          {listing.price > 0 ? `$${listing.price.toLocaleString()} rehoming fee` : "Free to good home"}
        </p>
        <div className="flex items-center gap-2 text-sm text-[#A1A1AA]">
          <span>{listing.breed}</span>
          {listing.age && (
            <>
              <span>•</span>
              <span>{listing.age}</span>
            </>
          )}
        </div>
        <div className="flex items-center justify-between mt-2">
          <div className="flex items-center gap-1 text-xs text-[#71717A]">
            <MapPin className="w-3 h-3" />
            <span>{listing.location_city}, {listing.location_state}</span>
          </div>
          {listing.breeder_name && (
            <span className="text-xs text-[#71717A]">
              {listing.is_shelter ? "🏠" : "🏷️"} {listing.breeder_name}
            </span>
          )}
        </div>
        <div className="flex flex-wrap gap-1 mt-3">
          {listing.good_with_dogs && (
            <span className="px-2 py-0.5 bg-[#27272A] text-[#A1A1AA] text-xs rounded">Dogs OK</span>
          )}
          {listing.good_with_cats && (
            <span className="px-2 py-0.5 bg-[#27272A] text-[#A1A1AA] text-xs rounded">Cats OK</span>
          )}
          {listing.house_trained && (
            <span className="px-2 py-0.5 bg-[#27272A] text-[#A1A1AA] text-xs rounded">House Trained</span>
          )}
        </div>
      </div>
    </Link>
  );
};

export default Adoptions;
